import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { Container, Row, Col, Card, Alert, Spinner, Badge, Button, ButtonGroup } from "react-bootstrap";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, TimeScale, Filler } from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { format, subDays, startOfDay } from "date-fns";
import { faCompress as compressIcon, faExpand as expandIcon, faSun, faMoon, faCircleHalfStroke } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'chartjs-adapter-date-fns';
import { ru } from "date-fns/locale";
import buildVer from "../../../ver.json";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, TimeScale, Filler);

const BUILD_VER = buildVer.ver;

const API_BASE = "/api/external-data";

interface PublicTrackedValue {
    timestamp: number;
    value: number;
}

interface PublicTrackerInfo {
    tracker_uuid: string;
    tracker_data: Record<string, unknown>;
    custom_title: string;
    order: number;
    lifepart_color: string | null;
    tracked_values: PublicTrackedValue[];
}

interface PublicTrackerPage {
    slug: string;
    title: string;
    text: string;
    trackers: PublicTrackerInfo[];
}

interface TrackerChartProps {
    tracker: PublicTrackerInfo;
}

function TrackerChart({ tracker }: TrackerChartProps) {
    const trackerData = tracker.tracker_data;
    const trackerName = (trackerData.name as string) || `Трекер`;
    const measure = (trackerData.measure as string) || "ед.";
    const target = (trackerData.target as number) || 0;
    const targetIndicator = (trackerData.targetIndicator as "MORE" | "LESS") || "MORE";
    const display = (trackerData.display as "SIMPLE" | "SUM" | "AVG") || "SIMPLE";
    const group = (trackerData.group as "NO" | "WEEK" | "MONTH") || "NO";
    const chartModeSetting = (trackerData.chartMode as "LAST14" | "ALL") || "LAST14";

    const [showAll, setShowAll] = useState(chartModeSetting === "ALL");

    const lifepartColor = tracker.lifepart_color || "#0d6efd";

    const sortedValues = [...tracker.tracked_values].sort((a, b) => a.timestamp - b.timestamp);

    const isSimple = display === "SIMPLE";
    const hasTarget = target !== undefined && target !== 0;

    const isTargetReached = useCallback((value: number): boolean => {
        if (targetIndicator === "MORE") {
            return value >= target;
        } else {
            return value <= target;
        }
    }, [target, targetIndicator]);

    const chartData = useMemo(() => {
        if (isSimple) {
            let days: { date: Date; timestamp: number; label: string }[];

            if (showAll) {
                if (sortedValues.length === 0) {
                    days = [];
                } else {
                    const firstDay = startOfDay(new Date(sortedValues[0].timestamp));
                    const lastDay = startOfDay(new Date());
                    const dayCount = Math.min(Math.ceil((lastDay.getTime() - firstDay.getTime()) / (24 * 60 * 60 * 1000)) + 1, 365);
                    days = Array.from({ length: dayCount }, (_, i) => {
                        const date = new Date(firstDay.getTime() + i * 24 * 60 * 60 * 1000);
                        return {
                            date,
                            timestamp: date.getTime(),
                            label: format(date, "dd.MM")
                        };
                    });
                }
            } else {
                days = Array.from({ length: 14 }, (_, i) => {
                    const date = subDays(startOfDay(new Date()), 13 - i);
                    return {
                        date,
                        timestamp: date.getTime(),
                        label: format(date, "dd.MM")
                    };
                });
            }

            const dataPoints = days.map((day) => {
                const value = sortedValues.find(
                    (tv) => startOfDay(new Date(tv.timestamp)).getTime() === day.timestamp
                );
                return value !== undefined ? value.value : null;
            });

            const pointColors = dataPoints.map((value) => {
                if (value === null) return `${lifepartColor}40`;
                if (!hasTarget) return lifepartColor;
                return isTargetReached(value) ? '#28a745' : '#dc3545';
            });

            const datasets: any[] = [
                {
                    label: tracker.custom_title || trackerName,
                    data: dataPoints,
                    borderColor: lifepartColor,
                    backgroundColor: `${lifepartColor}40`,
                    tension: 0.3,
                    fill: true,
                    spanGaps: true,
                    pointRadius: showAll ? 3 : 5,
                    pointHoverRadius: showAll ? 5 : 7,
                    pointBackgroundColor: pointColors,
                    pointBorderColor: pointColors.map(c =>
                        c === '#28a745' ? '#1e7e34' :
                        c === '#dc3545' ? '#bd2130' :
                        c === `${lifepartColor}40` ? c : '#fff'
                    ),
                    pointBorderWidth: 2
                }
            ];

            if (hasTarget) {
                datasets.push({
                    label: 'Цель',
                    data: Array(days.length).fill(target),
                    borderColor: '#6c757d',
                    borderDash: [5, 5],
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                    tension: 0
                });
            }

            return {
                labels: days.map((d) => d.label),
                datasets
            };
        } else {
            const groupByWeek = group === "WEEK";
            const now = new Date();
            const periodsCount = 12;

            const periods = Array.from({ length: periodsCount }, (_, i) => {
                const periodEnd = groupByWeek
                    ? subDays(startOfDay(new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)), 0)
                    : new Date(now.getFullYear() - Math.floor(i / 12), now.getMonth() - (i % 12), 1);
                const periodStart = groupByWeek
                    ? startOfDay(periodEnd)
                    : new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1);
                const periodEndDate = groupByWeek
                    ? new Date(periodStart.getTime() + 6 * 24 * 60 * 60 * 1000)
                    : new Date(periodEnd.getFullYear(), periodEnd.getMonth() + 1, 0);

                return {
                    start: periodStart.getTime(),
                    end: periodEndDate.getTime(),
                    label: groupByWeek
                        ? `Неделя ${format(periodStart, "dd.MM")}`
                        : format(periodStart, "MMM yyyy", { locale: ru })
                };
            }).reverse();

            const dataPoints = periods.map((period) => {
                const valuesInPeriod = sortedValues.filter(
                    (tv) => tv.timestamp >= period.start && tv.timestamp <= period.end
                );

                if (valuesInPeriod.length === 0) return null;

                const sum = valuesInPeriod.reduce((acc, v) => acc + v.value, 0);

                if (display === "SUM") {
                    return sum;
                } else if (display === "AVG") {
                    return sum / valuesInPeriod.length;
                } else {
                    return sum;
                }
                return null;
            });

            const barColors = dataPoints.map((value) => {
                if (value === null) return '#e0e0e0';
                if (!hasTarget) return `${lifepartColor}80`;
                return isTargetReached(value) ? '#28a745' : '#dc3545';
            });

            const barBorderColors = dataPoints.map((value) => {
                if (value === null) return '#c0c0c0';
                if (!hasTarget) return lifepartColor;
                return isTargetReached(value) ? '#1e7e34' : '#bd2130';
            });

            const datasets: any[] = [
                {
                    label: tracker.custom_title || trackerName,
                    data: dataPoints,
                    backgroundColor: barColors,
                    borderColor: barBorderColors,
                    borderWidth: 1,
                    borderRadius: 4,
                    maxBarThickness: 50
                }
            ];

            if (hasTarget) {
                datasets.push({
                    type: 'line' as const,
                    label: 'Цель',
                    data: Array(periods.length).fill(target),
                    borderColor: '#6c757d',
                    borderDash: [5, 5],
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                    tension: 0
                });
            }

            return {
                labels: periods.map((p) => p.label),
                datasets
            };
        }
    }, [sortedValues, isSimple, display, group, showAll, lifepartColor, hasTarget, target, targetIndicator, tracker.custom_title, trackerName]);

    const chartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.parsed.y} ${measure}`
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: isSimple ? false : true
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: '#e0e0e0'
                }
            }
        }
    };

    return (
        <Card className="mb-3">
            <Card.Header style={{ backgroundColor: lifepartColor + "20" }}>
                <Row className="align-items-center">
                    <Col>
                        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-between">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="fw-bold">{tracker.custom_title || trackerName}</span>
                                <Badge bg="secondary">{measure}</Badge>
                                {hasTarget && (
                                    <Badge bg={targetIndicator === "MORE" ? "success" : "warning"}>
                                        {targetIndicator === "MORE" ? "≥" : "≤"} {target}
                                    </Badge>
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-1 flex-wrap">
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => setShowAll(!showAll)}
                                    title={showAll ? "Показать последние 14" : "Показать все"}
                                >
                                    <FontAwesomeIcon icon={showAll ? compressIcon : expandIcon} />
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                <div style={{ height: 200 }}>
                    {isSimple ? (
                        <Line data={chartData} options={chartOptions} />
                    ) : (
                        <Bar data={chartData} options={chartOptions} />
                    )}
                </div>
            </Card.Body>
        </Card>
    );
}

export default function PublicTrackerPage() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [page, setPage] = useState<PublicTrackerPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [theme, setTheme] = useState<"light" | "dark" | "auto">("auto");

    // Автообновление при наличии нового SW
    useEffect(() => {
        const checkForUpdate = async () => {
            try {
                const response = await fetch('/ver.json?t=' + Date.now(), {
                    cache: 'no-cache',
                    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                });
                if (!response.ok) return;
                const data = await response.json();
                if (data.ver !== BUILD_VER) {
                    console.log('Доступно обновление:', data.ver);
                    if ('serviceWorker' in navigator) {
                        const reg = await navigator.serviceWorker.ready;
                        reg.update();
                    }
                }
            } catch (e) {
                console.debug('Проверка обновления не удалась:', e);
            }
        };

        checkForUpdate();
        const timer = window.setInterval(checkForUpdate, 60 * 1000);
        return () => clearInterval(timer);
    }, []);

    // onNeedRefresh — SW готов, перезагружаем страницу
    useEffect(() => {
        const handleNeedRefresh = () => {
            console.log('Новый SW готов к активации, перезагрузка...');
            window.location.reload();
        };
        window.onNeedRefresh = handleNeedRefresh;
        return () => { window.onNeedRefresh = undefined; };
    }, []);

    useEffect(() => {
        const saved = localStorage.getItem("tracker-theme");
        if (saved === "light" || saved === "dark" || saved === "auto") {
            setTheme(saved);
        }
    }, []);

    useEffect(() => {
        const resolved = theme === "auto" ? 
            (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : 
            theme;
        document.documentElement.setAttribute("data-bs-theme", resolved);
        localStorage.setItem("tracker-theme", theme);
    }, [theme]);

    const fetchPage = useCallback(async () => {
        if (!slug) return;
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${API_BASE}/public/pages/${slug}`);
            if (!response.ok) {
                if (response.status === 404) {
                    setError("Страница не найдена");
                } else {
                    setError("Ошибка загрузки страницы");
                }
                return;
            }
            const data: PublicTrackerPage = await response.json();
            setPage(data);
        } catch (err) {
            console.error("Failed to fetch page:", err);
            setError("Ошибка подключения к серверу");
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchPage();
    }, [fetchPage]);

    if (loading) {
        return (
            <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (error || !page) {
        return (
            <Container fluid className="mt-4">
                <Row>
                    <Col>
                        <Alert variant="danger">
                            <Alert.Heading>Ошибка</Alert.Heading>
                            <p>{error || "Страница не найдена"}</p>
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    }

    const sortedTrackers = [...page.trackers].sort((a, b) => a.order - b.order);

    return (
        <Container fluid className="px-4">
            <Row>
                <Col>
                    <div className="mt-4">
                        <Card>
                            <Card.Header>
                                <Row className="align-items-center">
                                    <Col>
                                        <h2 className="mb-0">{page.title}</h2>
                                    </Col>
                                    <Col xs="auto">
                                        <ButtonGroup size="sm">
                                            <Button
                                                variant={theme === "auto" ? "secondary" : "outline-secondary"}
                                                onClick={() => setTheme("auto")}
                                                title="Авто тема"
                                            >
                                                <FontAwesomeIcon icon={faCircleHalfStroke} />
                                            </Button>
                                            <Button
                                                variant={theme === "light" ? "secondary" : "outline-secondary"}
                                                onClick={() => setTheme("light")}
                                                title="Светлая тема"
                                            >
                                                <FontAwesomeIcon icon={faSun} />
                                            </Button>
                                            <Button
                                                variant={theme === "dark" ? "secondary" : "outline-secondary"}
                                                onClick={() => setTheme("dark")}
                                                title="Тёмная тема"
                                            >
                                                <FontAwesomeIcon icon={faMoon} />
                                            </Button>
                                        </ButtonGroup>
                                    </Col>
                                </Row>
                            </Card.Header>
                            <Card.Body>
                                {page.text && (
                                    <div className="mb-4">
                                        <Markdown remarkPlugins={[remarkGfm]}>{page.text}</Markdown>
                                    </div>
                                )}

                                {sortedTrackers.length === 0 ? (
                                    <Alert variant="info">Трекеров пока нет.</Alert>
                                ) : (
                                    <>
                                        {sortedTrackers.map((tracker) => (
                                            <TrackerChart key={tracker.tracker_uuid} tracker={tracker} />
                                        ))}
                                    </>
                                )}
                            </Card.Body>
                            <Card.Footer className="text-center">
                                <Button variant="primary" onClick={() => navigate("/")}>
                                    Создать свои трекеры
                                </Button>
                            </Card.Footer>
                        </Card>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}
